<template>
  <!-- ----------------- -->
  <!-- TOP ROW (HEADER)  -->
  <!-- ----------------- -->
  <div>
    <!-- ------------------------------------------------------------>
    <!-- MAIN CONTAINER -->
    <!-- -------------- -->
    <div
      v-if="isReady"
      class="row gx-1"
    >
      <!-- ------------------------------------------------------- -->
      <!--   FAV SKILLS   -->
      <!-- ----------------->
      <div
        class="col-6 divider-column"
        v-for="column in favouriteColumns"
        :key="column.key"
      >
        <!-- ----------------------------------------------------- -->
        <!-- table headings -->
        <!-- ----------------------------------------------------- -->
        <div class="row gx-1">
          <div class="col-8 text-center">
            <p class="bb-hdg mt-2 mb-3">Skill</p>
          </div>
          <div class="col-4 text-center">
            <p class="bb-hdg mt-2 mb-3">Cur</p>
          </div>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- data rows -->
        <!-- ----------------------------------------------------- -->
        <div
          class="row gx-1 mb-2 align-items-center bb-tooltip-wrapper"
          v-for="fav in column.items"
          :key="getFavKey(fav)"
        >
          <!-- ----------------------------------------------------- -->
          <!-- skill -->
          <!-- ----------------------------------------------------- -->
          <div class="col-8 text-center">
            <div class="form-group">
              <component
                :is="isFavNameOverflowing(fav) ? 'ToolTip' : 'div'"
                v-bind="
                  isFavNameOverflowing(fav)
                    ? {
                        text: getFavName(fav),
                        position: 'bottom',
                        tableWrapperMode: true,
                        maxWidth: '520px',
                      }
                    : {}
                "
                style="display: block"
              >
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-left"
                    :value="getFavName(fav)"
                    :id="`fav-${getFavKey(fav)}`"
                    placeholder=" "
                    disabled
                  />
                </div>
              </component>
            </div>
          </div>
          <!-- ----------------------------------------------------- -->
          <!-- current %age -->
          <!-- ----------------------------------------------------- -->
          <div class="col-4 text-center">
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="fav.cur"
                  :id="`fav-cur-${getFavKey(fav)}`"
                  placeholder=" "
                  disabled
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { inject, nextTick } from 'vue'

import ToolTip from '../components/ToolTip.vue'

export default {
  name: 'SheetFavourites',
  //========================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
    favs: {
      type: Array,
      required: true,
    },
  },
  //========================================================
  // components
  //------------
  components: {
    ToolTip,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // data
  //------
  data() {
    return {
      local_character: null,
      overflowingFavKeys: {},
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    character: {
      handler(newChar) {
        if (!newChar) return
        this.local_character = this.character
        this.refreshOverflowState()
      },
      deep: true,
      immediate: true,
    },
    favs: {
      handler() {
        this.refreshOverflowState()
      },
      deep: true,
      immediate: true,
    },
  },
  //========================================================
  // computed
  //-----------
  computed: {
    isReady() {
      if (this.local_character != null) {
        return true
      }
      return false
    },
    cores() {
      if (!this.favs) return []
      return this.favs.filter((fav) => this.isCoreFavourite(fav)) || []
    },
    nonCores() {
      if (!this.favs) return []
      return this.favs
        .filter((fav) => !this.isCoreFavourite(fav))
        .slice()
        .sort((left, right) => {
          const leftCategory = `${left?.category || ''}`.trim()
          const rightCategory = `${right?.category || ''}`.trim()
          const categoryCompare = leftCategory.localeCompare(rightCategory)

          if (categoryCompare !== 0) {
            return categoryCompare
          }

          const leftName = this.getFavName(left)
          const rightName = this.getFavName(right)
          return leftName.localeCompare(rightName)
        })
    },
    favouriteColumns() {
      return [
        {
          key: 'core',
          items: this.cores,
        },
        {
          key: 'non-core',
          items: this.nonCores,
        },
      ]
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    getFavKey(fav) {
      return fav?.id || fav?.skill || fav?.name || 'fav'
    },
    isCoreFavourite(fav) {
      return `${fav?.category || ''}`.trim().toLowerCase() === 'core'
    },
    getFavName(fav) {
      const displayName = fav?.display_name?.trim()
      if (displayName) {
        return displayName
      }
      return fav?.name || ''
    },
    isFavNameOverflowing(fav) {
      return !!this.overflowingFavKeys[this.getFavKey(fav)]
    },
    refreshOverflowState() {
      nextTick(() => {
        const overflowState = {}

        this.favouriteColumns.forEach((column) => {
          column.items.forEach((fav) => {
            const favKey = this.getFavKey(fav)
            const input = document.getElementById(`fav-${favKey}`)

            overflowState[favKey] = input
              ? input.scrollWidth > input.clientWidth
              : false
          })
        })

        this.overflowingFavKeys = overflowState
      })
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    window.addEventListener('resize', this.refreshOverflowState)
    this.refreshOverflowState()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.refreshOverflowState)
  },
}
</script>

<style scoped>
.divider-column {
  position: relative;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

.divider-column::after {
  content: '';
  position: absolute;
  top: 0;
  background-color: #575656;
  bottom: 0;
  right: 0;
  width: 3px;
  pointer-events: none;
}
</style>
